import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import posthog from "posthog-js";
import { v4 as uuid } from "uuid";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { useLaunchableDeployQuery } from "components/Launchables/DeployRedesign/shared/hooks/useLaunchableDeployQuery";
import {
  getLaunchableDeployOverrides,
  getLaunchableIdFromSearch,
} from "components/Launchables/DeployRedesign/shared/page/queryParams";
import { DeployPageContent } from "components/Launchables/DeployRedesign/shared/page/DeployPageContent";
import { validateTunnelName } from "components/utils";
import type { LaunchableParameterBinding, NGCAPIKeySpec } from "server";
import { useInstanceTypeQuery } from "server/devplane/instanceTypes/queries";
import { useLaunchableLifecycleScriptQuery } from "server/brevdeploy/launchables/queries";
import { parseInstanceType } from "utils/instanceTypes";
import { useRedirectToCreatedInstance } from "components/Launchables/DeployRedesign/shared/hooks/useRedirectToCreatedInstance";
import { useLaunchableComputeSelection } from "components/Launchables/DeployRedesign/shared/hooks/useLaunchableComputeSelection";
import { createLaunchableDeploymentData } from "components/Launchables/DeployRedesign/shared/deployment/launchableDeploymentData";

const generateInstanceName = (launchableName: string) =>
  launchableName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase() +
  "-" +
  uuid().substring(0, 6);

const LaunchableDeploy: React.FC = () => {
  const location = useLocation();
  const queryLaunchableID = getLaunchableIdFromSearch(location.search);
  const deployOverrides = useMemo(
    () => getLaunchableDeployOverrides(location.search),
    [location.search],
  );
  const {
    launchableData,
    rawLaunchableData,
    loading,
    retrying,
    launchableNotFound,
    launchableAccessError,
    launchableLoadError,
    refetch,
  } = useLaunchableDeployQuery(queryLaunchableID || "", false);

  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const workspaceContext = useContext(WorkspaceContext);
  const [editedInstanceName, setEditedInstanceName] = useState<{
    launchableId: string;
    value: string;
  } | null>(null);
  const [instanceNameError, setInstanceNameError] = useState<{
    launchableId: string;
    value: string;
  } | null>(null);
  const viewedLaunchableId = useRef<string | null>(null);
  const oneClickContext = useContext(OneClickDeployContext);
  const launchableName = launchableData?.name;

  const generatedInstanceName = useMemo(
    () =>
      queryLaunchableID && launchableName
        ? generateInstanceName(launchableName)
        : "",
    [launchableName, queryLaunchableID],
  );
  const instanceName =
    editedInstanceName?.launchableId === queryLaunchableID
      ? editedInstanceName.value
      : generatedInstanceName;
  const nameErrorMessage =
    instanceNameError?.launchableId === queryLaunchableID
      ? instanceNameError.value
      : "";

  const { beginRedirect: beginInstanceRedirect, cancelRedirect } =
    useRedirectToCreatedInstance({
      environmentId: oneClickContext.environment?.workspaceId,
      organizationId: orgContext.activeOrgId,
    });

  const lifecycleScriptId =
    launchableData?.containerSelected?.vmBuild?.lifeCycleScriptAttr?.id;
  const {
    data: lifecycleScript = null,
    isInitialLoading: lifecycleScriptLoading,
  } = useLaunchableLifecycleScriptQuery(
    queryLaunchableID || "",
    lifecycleScriptId || "",
  );
  const intendedInstanceType =
    deployOverrides.instanceType ?? launchableData?.instanceType ?? null;
  const intendedCloudCredentialId =
    deployOverrides.cloudCredentialId ?? launchableData?.cloudCredId ?? null;
  const intendedDiskStorage =
    deployOverrides.diskStorage ?? launchableData?.diskStorage ?? null;
  // Preserve explicit "?location=any" (null) vs unset (undefined) -- matches DeployPageContent.
  const intendedLocation =
    deployOverrides.location !== undefined
      ? deployOverrides.location
      : (launchableData?.location ?? null);
  const parsedInstanceType =
    parseInstanceType(intendedInstanceType)?.instanceType ?? "";
  const { data: intendedInstance, isInitialLoading: loadingInstance } =
    useInstanceTypeQuery(
      parsedInstanceType,
      intendedCloudCredentialId || "",
      intendedLocation,
    );
  const {
    instanceType: currentInstanceTypeObject,
    storage: currentStorage,
    setSelection: handleSetNewCurrentInstanceType,
  } = useLaunchableComputeSelection({
    launchableId: queryLaunchableID || "",
    intendedInstance,
    intendedStorage: intendedDiskStorage,
  });

  const extraEventProperties = useMemo(
    () => ({
      launchableID: queryLaunchableID,
      launchableInstanceType: launchableData?.instanceType,
      cloudCredID: launchableData?.cloudCredId,
      launchableCreatedByUserID: launchableData?.userId,
      launchableCreatedByOrgID: launchableData?.orgId,
      launchableRawURL: location.pathname + location.search,
    }),
    [launchableData, location.pathname, location.search, queryLaunchableID],
  );
  useEffect(() => {
    if (
      !launchableData ||
      !queryLaunchableID ||
      viewedLaunchableId.current === queryLaunchableID
    ) {
      return;
    }

    viewedLaunchableId.current = queryLaunchableID;
    posthog.capture("Launchable Viewed", extraEventProperties);
  }, [extraEventProperties, launchableData, queryLaunchableID]);

  const handleDeploy = useCallback(
    (
      ngcApiKeySpec?: NGCAPIKeySpec | null,
      parameterBindings?: LaunchableParameterBinding[],
      selectedLocation?: string | null,
    ) => {
      const deploymentData = createLaunchableDeploymentData({
        launchableData,
        instanceType: currentInstanceTypeObject,
        storage: currentStorage,
        selectedLocation,
        lifecycleScript,
      });

      beginInstanceRedirect();
      return oneClickContext
        .startDeploymentFromLaunchable(
          deploymentData,
          extraEventProperties,
          instanceName,
          ngcApiKeySpec,
          parameterBindings,
        )
        .then((result) => {
          if (result?.deploymentStarted === false) {
            cancelRedirect();
          }
          return result;
        })
        .catch((error) => {
          cancelRedirect();
          throw error;
        });
    },
    [
      beginInstanceRedirect,
      cancelRedirect,
      currentInstanceTypeObject,
      currentStorage,
      extraEventProperties,
      instanceName,
      launchableData,
      lifecycleScript,
      oneClickContext,
    ],
  );

  return (
    <DeployPageContent
      key={queryLaunchableID}
      launchableId={queryLaunchableID}
      intendedInstanceType={intendedInstanceType}
      intendedCloudCredId={intendedCloudCredentialId}
      intendedDiskStorage={intendedDiskStorage}
      defaultInstanceType={intendedInstance ?? null}
      onSetNewCurrentInstanceType={handleSetNewCurrentInstanceType}
      loading={loading || lifecycleScriptLoading || loadingInstance}
      launchableNotFound={launchableNotFound}
      launchableAccessError={launchableAccessError}
      launchableLoadError={launchableLoadError}
      retryingLaunchable={retrying}
      onRetryLaunchable={() => {
        void refetch();
      }}
      launchableData={launchableData}
      rawLaunchableData={rawLaunchableData}
      instanceLoading={loadingInstance}
      instanceTypeObject={currentInstanceTypeObject}
      instanceStorage={currentStorage || intendedDiskStorage || ""}
      nameErrorMessage={nameErrorMessage}
      instanceName={instanceName}
      onInstanceNameChange={(name) => {
        if (!queryLaunchableID) return;
        setEditedInstanceName({ launchableId: queryLaunchableID, value: name });
        const validationError = validateTunnelName(name);
        if (validationError) {
          setInstanceNameError({
            launchableId: queryLaunchableID,
            value: validationError,
          });
        } else if (workspaceContext.environments.some((e) => e.name === name)) {
          setInstanceNameError({
            launchableId: queryLaunchableID,
            value: "An instance with this name already exists",
          });
        } else {
          setInstanceNameError({
            launchableId: queryLaunchableID,
            value: "",
          });
        }
      }}
      allInstancesAvailable={instanceTypesContext.allInstancesAvailable}
      onDeploy={handleDeploy}
      lifecycleScript={lifecycleScript}
      initialLocation={deployOverrides.location}
    />
  );
};

export default LaunchableDeploy;
